import Link from 'next/link';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import ForumAvatar from '@/components/forum/ForumAvatar';
import {
  clearForumSession,
  FORUM_SESSION_EVENT,
  getForumSession,
  getUserDisplayName,
  type ForumSession,
} from '@/lib/forumClient';

type MenuLink = {
  href: string;
  label: string;
  description?: string;
};

type MenuGroup = {
  id: string;
  label: string;
  href?: string;
  featured?: boolean;
  links: MenuLink[];
};

const menuGroups: MenuGroup[] = [
  {
    id: 'company',
    label: 'Our Mission',
    links: [
      {
        href: '/about',
        label: 'About Us',
        description: 'What DocMoonlight is building for modern imaging.',
      },
      {
        href: '/founder',
        label: 'Our Founder',
        description: 'Meet the physician behind the platform.',
      },
    ],
  },
  {
    id: 'offerings',
    label: 'Radiology Centers',
    links: [
      {
        href: '/what-we-offer',
        label: 'What We Offer',
        description: 'Overview of coverage models for imaging operations.',
      },
      {
        href: '/virtual-coverage',
        label: 'Virtual',
        description: 'Remote physician support for routine coverage.',
      },
      {
        href: '/onsite-coverage',
        label: 'Onsite',
        description: 'In-person physician presence where it matters.',
      },
      {
        href: '/hybrid-coverage',
        label: 'Hybrid',
        description: 'Blend virtual and onsite coverage by workflow.',
      },
      {
        href: '/teleradiology',
        label: 'Teleradiology',
        description: 'Remote reading support for imaging center operations.',
      },
    ],
  },
  {
    id: 'infusion',
    label: 'Infusion Centers',
    href: '/infusion',
    featured: true,
    links: [],
  },
  {
    id: 'residents',
    label: 'Residents & Fellows',
    href: '/residents',
    featured: true,
    links: [],
  },
  {
    id: 'resources',
    label: 'Resources',
    links: [
      {
        href: '/forum',
        label: 'Forum',
        description: 'Text-first community posts and comment threads.',
      },
      {
        href: '/blog',
        label: 'Blogs',
        description: 'Practical guidance for operators and leaders.',
      },
      {
        href: '/news',
        label: 'In The News',
        description: 'Relevant updates around DocMoonlight and imaging.',
      },
    ],
  },
  {
    id: 'contact',
    label: 'Contact',
    href: '/contact#contact-form',
    links: [],
  },
];

const isRouteActive = (pathname: string, href: string) => {
  const route = href.split('#')[0];

  if (route === '/') {
    return pathname === '/';
  }

  return pathname === route || pathname.startsWith(`${route}/`);
};

const isGroupActive = (pathname: string, group: MenuGroup) =>
  Boolean(
    group.href
      ? isRouteActive(pathname, group.href)
      : group.links.some((link) => isRouteActive(pathname, link.href)),
  );

const getGroupHref = (group: MenuGroup) =>
  group.href || group.links[0]?.href || '/';

const groupButtonClass = (active: boolean, featured?: boolean) => {
  return `inline-flex min-h-[3.5rem] items-center rounded-[1.25rem] border px-4 py-3 text-sm font-semibold transition duration-200 ${
    active
      ? featured
        ? 'border-transparent bg-transparent text-[#60a5fa]'
        : 'border-transparent bg-transparent text-[#60a5fa]'
      : featured
        ? 'border-transparent text-white hover:text-[#60a5fa]'
        : 'border-transparent text-[rgba(255,255,255,0.78)] hover:text-[#60a5fa]'
  }`;
};

const desktopDropdownLinkClass = (active: boolean) =>
  `block rounded-lg border px-3 py-2 text-sm font-semibold transition duration-200 ${
    active
      ? 'border-transparent bg-transparent text-[#60a5fa]'
      : 'border-transparent text-[rgba(255,255,255,0.75)] hover:text-[#60a5fa]'
  }`;

const SiteNavbar = () => {
  const router = useRouter();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeDesktopMenu, setActiveDesktopMenu] = useState<string | null>(
    null,
  );
  const [openMobileSections, setOpenMobileSections] = useState<string[]>([]);
  const [session, setSession] = useState<ForumSession | null>(null);

  useEffect(() => {
    const syncSession = () => {
      setSession(getForumSession());
    };

    syncSession();
    window.addEventListener(FORUM_SESSION_EVENT, syncSession);
    window.addEventListener('storage', syncSession);

    return () => {
      window.removeEventListener(FORUM_SESSION_EVENT, syncSession);
      window.removeEventListener('storage', syncSession);
    };
  }, []);

  const userName = getUserDisplayName(session);

  const handleLogout = () => {
    clearForumSession();
    setMobileOpen(false);
    router.push('/forum');
  };

  const toggleMobileSection = (groupId: string) => {
    setOpenMobileSections((current) =>
      current.includes(groupId) ? [] : [groupId],
    );
  };

  return (
    <header className='relative z-50 bg-transparent'>
      <div className='mx-auto max-w-7xl px-4 py-4 sm:px-6 lg:px-1'>
        <div className='md:hidden'>
          <div className='flex items-center justify-between gap-4 rounded-[1.75rem] border border-[rgba(255,255,255,0.08)] bg-[rgba(17,24,39,0.96)] px-4 py-3 shadow-[0_18px_50px_rgba(2,6,23,0.24)]'>
            <Link href='/' className='flex items-center text-white'>
              <Image
                src='/images/dark-logo.png'
                alt='DocMoonlight logo'
                width={110}
                height={76}
                sizes='(max-width: 767px) 82px, 94px'
                className='h-14 w-auto max-w-[180px] object-contain'
                priority
              />
            </Link>

            <button
              type='button'
              onClick={() => setMobileOpen((open) => !open)}
              aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
              className='inline-flex h-12 w-12 items-center justify-center rounded-full border border-[rgba(255,255,255,0.12)] bg-[rgba(255,255,255,0.08)] text-white'
            >
              <svg
                className='h-5 w-5'
                viewBox='0 0 24 24'
                fill='none'
                stroke='currentColor'
                strokeWidth='2'
                strokeLinecap='round'
                strokeLinejoin='round'
                aria-hidden='true'
              >
                {mobileOpen ? (
                  <path d='M6 6l12 12M18 6L6 18' />
                ) : (
                  <path d='M4 7h16M4 12h16M4 17h16' />
                )}
              </svg>
            </button>
          </div>
        </div>

        <div
          className='relative hidden md:block'
          onMouseLeave={() => setActiveDesktopMenu(null)}
        >
          <div className='rounded-[2rem] border border-[rgba(96,165,250,0.16)] bg-[rgba(17,24,39,0.97)] p-3 shadow-[0_24px_80px_rgba(2,6,23,0.28)] backdrop-blur'>
            <nav className='flex items-center gap-3'>
              <Link
                href='/'
                className='mr-2 inline-flex min-h-[3.5rem] items-center rounded-[1.35rem] border border-transparent px-3 py-2 text-white transition hover:text-[#60a5fa]'
              >
                <Image
                  src='/images/dark-logo.png'
                  alt='DocMoonlight logo'
                  width={110}
                  height={76}
                  sizes='94px'
                  className='h-16 w-auto max-w-[220px] shrink-0 object-contain'
                  priority
                />
              </Link>

              <div className='ml-auto flex flex-wrap items-center justify-end gap-2'>
                {menuGroups.map((group) => {
                  const active = isGroupActive(router.pathname, group);
                  const isMenuOnly = group.links.length > 0 && !group.href;

                  return (
                    <div
                      key={group.id}
                      className='relative'
                      onMouseEnter={() => setActiveDesktopMenu(group.id)}
                      onFocus={() => setActiveDesktopMenu(group.id)}
                    >
                      {isMenuOnly ? (
                        <button
                          type='button'
                          className={groupButtonClass(active, group.featured)}
                          onClick={() =>
                            setActiveDesktopMenu((current) =>
                              current === group.id ? null : group.id,
                            )
                          }
                          aria-expanded={activeDesktopMenu === group.id}
                        >
                          {group.label}
                        </button>
                      ) : (
                        <Link
                          href={getGroupHref(group)}
                          className={groupButtonClass(active, group.featured)}
                          aria-expanded={
                            group.links.length > 0
                              ? activeDesktopMenu === group.id
                              : undefined
                          }
                        >
                          {group.label}
                        </Link>
                      )}

                      {activeDesktopMenu === group.id &&
                        group.links.length > 0 && (
                          <div className='absolute left-1/2 top-full z-[60] mt-2 -translate-x-1/2'>
                            <div className='rounded-xl border border-[#60a5fa]/18 bg-[rgba(17,24,39,0.98)] px-2 py-2 shadow-[0_18px_42px_rgba(2,6,23,0.34)]'>
                              <div className='space-y-0.5 whitespace-nowrap'>
                                {group.links.map((link) => {
                                  const active = isRouteActive(
                                    router.pathname,
                                    link.href,
                                  );

                                  return (
                                    <Link
                                      key={link.href + link.label}
                                      href={link.href}
                                      className={desktopDropdownLinkClass(
                                        active,
                                      )}
                                    >
                                      {link.label}
                                    </Link>
                                  );
                                })}
                              </div>
                            </div>
                          </div>
                        )}
                    </div>
                  );
                })}

                {session && (
                  <div className='relative pl-2'>
                    <button
                      type='button'
                      className='flex h-12 items-center gap-2 rounded-full border border-[rgba(255,255,255,0.08)] bg-[rgba(255,255,255,0.06)] px-2.5 py-2 text-left transition hover:border-[#60a5fa]/40 hover:text-[#60a5fa]'
                      onClick={() =>
                        setActiveDesktopMenu((current) =>
                          current === 'forum-user' ? null : 'forum-user',
                        )
                      }
                      aria-expanded={activeDesktopMenu === 'forum-user'}
                    >
                      <ForumAvatar
                        name={userName}
                        size='sm'
                        imageSrc={session.profilePic}
                      />
                      <div className='min-w-0 max-w-[92px]'>
                        <div className='truncate text-xs font-black text-white'>
                          {userName}
                        </div>
                      </div>
                    </button>

                    {activeDesktopMenu === 'forum-user' && (
                      <div className='absolute right-0 top-full z-[60] mt-2 w-56'>
                        <div className='rounded-xl border border-[#60a5fa]/18 bg-[rgba(17,24,39,0.98)] p-2 shadow-[0_18px_42px_rgba(2,6,23,0.34)]'>
                          <Link
                            href='/forum'
                            className='block rounded-lg border border-transparent px-3 py-2 text-sm font-semibold text-[rgba(255,255,255,0.76)] transition hover:text-[#60a5fa]'
                          >
                            Open forum
                          </Link>
                          <button
                            type='button'
                            onClick={handleLogout}
                            className='mt-1 block w-full rounded-lg border border-transparent px-3 py-2 text-left text-sm font-semibold text-white transition hover:text-[#60a5fa]'
                          >
                            Log out
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </nav>
          </div>
        </div>
      </div>

      {mobileOpen && (
        <nav className='relative z-[60] mx-4 mt-2 max-w-7xl rounded-[1.75rem] border border-[#60a5fa]/18 bg-[rgba(17,24,39,0.98)] px-4 py-5 shadow-[0_18px_50px_rgba(2,6,23,0.24)] sm:px-6 md:hidden'>
          <div className='flex flex-col gap-4'>
            {session && (
              <div className='rounded-[1.5rem] border border-[rgba(255,255,255,0.08)] bg-[rgba(255,255,255,0.06)] p-4'>
                <div className='flex items-center gap-3'>
                  <ForumAvatar
                    name={userName}
                    size='md'
                    imageSrc={session.profilePic}
                  />
                  <div className='min-w-0 flex-1'>
                    <div className='truncate text-sm font-black text-white'>
                      {userName}
                    </div>
                    <div className='truncate text-xs text-[rgba(255,255,255,0.65)]'>
                      {session.email}
                    </div>
                  </div>
                </div>
                <div className='mt-3 grid gap-2'>
                  <Link
                    href='/forum'
                    onClick={() => setMobileOpen(false)}
                    className='rounded-[1rem] border border-[rgba(255,255,255,0.1)] bg-[rgba(255,255,255,0.08)] px-4 py-3 text-sm font-semibold text-white'
                  >
                    Open forum
                  </Link>
                  <button
                    type='button'
                    onClick={handleLogout}
                    className='rounded-[1rem] border border-[rgba(255,255,255,0.08)] bg-[var(--theme-accent)] px-4 py-3 text-left text-sm font-semibold text-[var(--theme-dark)]'
                  >
                    Log out
                  </button>
                </div>
              </div>
            )}

            {menuGroups.map((group) => {
              const isOpen = openMobileSections.includes(group.id);
              const active = isGroupActive(router.pathname, group);
              const isMenuOnly = group.links.length > 0 && !group.href;

              return (
                <div
                  key={group.id}
                  className='rounded-[1.5rem] border border-[rgba(255,255,255,0.08)] bg-[rgba(255,255,255,0.06)] p-3'
                >
                  <div className='flex items-center gap-2'>
                    {isMenuOnly ? (
                      <button
                        type='button'
                        onClick={() => toggleMobileSection(group.id)}
                        className={`flex-1 rounded-[1rem] px-4 py-3 text-left text-sm font-black transition ${
                          active
                            ? 'bg-[rgba(255,255,255,0.08)] text-[#60a5fa]'
                            : 'bg-[rgba(255,255,255,0.08)] text-white hover:text-[#60a5fa]'
                        }`}
                        aria-expanded={isOpen}
                      >
                        {group.label}
                      </button>
                    ) : (
                      <Link
                        href={getGroupHref(group)}
                        onClick={() => setMobileOpen(false)}
                        className={`flex-1 rounded-[1rem] px-4 py-3 text-left text-sm font-black transition ${
                          active
                            ? 'bg-[rgba(255,255,255,0.08)] text-[#60a5fa]'
                            : 'bg-[rgba(255,255,255,0.08)] text-white hover:text-[#60a5fa]'
                        }`}
                      >
                        {group.label}
                      </Link>
                    )}
                    {group.links.length > 0 && (
                      <button
                        type='button'
                        onClick={() => toggleMobileSection(group.id)}
                        className='inline-flex h-[50px] w-[50px] items-center justify-center rounded-[1rem] border border-[rgba(255,255,255,0.1)] bg-[rgba(255,255,255,0.08)] text-[rgba(255,255,255,0.75)] transition hover:text-[#60a5fa]'
                        aria-expanded={isOpen}
                        aria-label={`Toggle ${group.label} menu`}
                      >
                        <svg
                          className='h-5 w-5'
                          viewBox='0 0 24 24'
                          fill='none'
                          stroke='currentColor'
                          strokeWidth='2'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          aria-hidden='true'
                        >
                          {isOpen ? (
                            <path d='M6 6l12 12M18 6L6 18' />
                          ) : (
                            <>
                              <path d='M4 7h16' />
                              <path d='M4 12h16' />
                              <path d='M4 17h16' />
                            </>
                          )}
                        </svg>
                      </button>
                    )}
                  </div>

                  {isOpen && (
                    <div className='mt-3 grid gap-2'>
                      {group.links.map((link) => {
                        const linkActive = isRouteActive(
                          router.pathname,
                          link.href,
                        );

                        return (
                          <Link
                            key={link.href + link.label}
                            href={link.href}
                            onClick={() => setMobileOpen(false)}
                            className={`rounded-[1rem] border px-4 py-3 transition ${
                              linkActive
                                ? 'border-[rgba(255,255,255,0.08)] bg-[rgba(255,255,255,0.06)] text-[#60a5fa]'
                                : 'border-[rgba(255,255,255,0.08)] bg-[rgba(255,255,255,0.06)] text-white hover:text-[#60a5fa]'
                            }`}
                          >
                            <div className='text-sm font-black'>
                              {link.label}
                            </div>
                            {link.description && (
                              <div
                                className={`mt-1 text-xs leading-6 ${
                                  linkActive
                                    ? 'text-[rgba(255,255,255,0.72)]'
                                    : 'text-[rgba(255,255,255,0.62)]'
                                }`}
                              >
                                {link.description}
                              </div>
                            )}
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </nav>
      )}
    </header>
  );
};

export default SiteNavbar;
